<template>
	<Dialog :open="open" @update:open="$emit('close')">
		<DialogContent :disable-overlay-close="disableOverlayClose" class="max-w-2xl gap-1" @close="$emit('close')">
			<DialogTitle>Bulk Upload Settings</DialogTitle>
			<DialogDescription>
				Review and select where to upload the {{ selectedFiles.length }} selected files.
			</DialogDescription>

			<div v-if="tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller')" class="mx-auto w-full">
				<div class="formGroup withIcon">
					<label class="textGrey">Tenant</label>
					<select v-model="selectedTenant" @change="selectedAccount = null">
						<option disabled :value="null">-- Select Tenant --</option>
						<option v-for="t in tenants" :key="t.id" :value="t.id">
							{{ t.companyName }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>
			<div v-if="selectedTenant && !hideAccountSelector" class="mx-auto w-full">
				<div class="formGroup withIcon">
					<label class="textGrey">Account</label>
					<select v-model="selectedAccount">
						<option :value="null">-- Unassigned - Tenant File --</option>
						<option v-for="a in tenantAccounts" :key="a.id" :value="a.id">
							{{ a.name }}
						</option>
					</select>
					<span class="fa-light fa-chevron-down"></span>
				</div>
			</div>

			<div class="w-full">
				<div class="formGroup py-2">
					<label class="textGrey">Select Files</label>
					<form enctype="multipart/form-data">
						<input
							ref="fileInput"
							type="file"
							accept="*/*"
							multiple
							name="files"
							@change="handleFileChange"
						/>
					</form>
				</div>

				<div v-if="selectedFiles.length > 0" class="w-full">
					<label class="textGrey">Selected Files ({{ selectedFiles.length }})</label>
					<div class="max-h-40 overflow-y-auto rounded-md border bg-gray-50 p-2">
						<div
							v-for="(file, index) in selectedFiles"
							:key="index"
							class="flex items-center justify-between py-1"
						>
							<span class="truncate text-sm">{{ file.name }}</span>
							<button class="text-red-500 hover:text-red-700" @click="removeFile(index)">
								<span class="fa-light fa-times"></span>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div class="formGroup">
				<button
					:disabled="!selectedTenant || selectedFiles.length === 0"
					class="jmiBtn green"
					@click="confirmUpload"
				>
					Upload {{ selectedFiles.length }} Files
				</button>
				<button class="jmiBtn grey ml-2" @click="$emit('close')">Cancel</button>
			</div>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import accountsApi from '@/scripts/accounts/accounts.api'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogTitle,
		DialogDescription
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: ['disableOverlayClose', 'selectedTenantId', 'selectedAccountId', 'open', 'loadedAccounts'],
	emits: ['close', 'confirm'],
	data() {
		return {
			accounts: [],
			selectedTenant: this.selectedTenantId || null,
			selectedAccount: this.selectedAccountId || null,
			hideTenantSelector: Boolean(this.selectedTenantId),
			hideAccountSelector: Boolean(this.selectedAccountId),
			selectedFiles: []
		}
	},
	computed: {
		tenantAccounts() {
			return this.accounts
				.filter((a) => a.tenant.id === this.selectedTenant)
				.sort((a, b) => a.name.localeCompare(b.name))
		}
	},
	watch: {
		open(newVal) {
			if (newVal) {
				this.selectedFiles = []

				if (this.selectedTenantId) {
					this.selectedTenant = this.selectedTenantId
				} else if (this.tenants?.length === 1) {
					this.selectedTenant = this.tenants[0].id
				}

				if (this.selectedAccountId) {
					this.selectedAccount = this.selectedAccountId
					this.hideAccountSelector = true
				} else {
					this.hideAccountSelector = false
				}

				this.loadAccounts()
			}
		}
	},
	async mounted() {
		await this.loadAccounts()
	},
	methods: {
		async loadAccounts() {
			try {
				if (!this.loadedAccounts) {
					const result = await accountsApi.getAll({ limit: 2500 })
					this.accounts = result.data.results
				} else {
					this.accounts = this.loadedAccounts
				}

				if (this.selectedAccountId && this.selectedTenantId) {
					this.selectedAccount = this.selectedAccountId
					this.hideAccountSelector = true
				}
			} catch (error) {
				console.error(error)
			}
		},
		confirmUpload() {
			this.$emit('confirm', {
				selectedTenant: this.selectedTenant,
				selectedAccount: this.selectedAccount,
				files: this.selectedFiles
			})
		},
		removeFile(index) {
			this.selectedFiles.splice(index, 1)
		},
		handleFileChange(event) {
			const files = Array.from(event.target.files)
			this.selectedFiles = files
		}
	}
}
</script>
